import { useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Link,
  CircularProgress, Alert, MenuItem, Radio, RadioGroup,
  FormControlLabel, FormControl, FormLabel,
} from '@mui/material';
import { Badge as BadgeIcon } from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import { NATIONALITY_OPTIONS, getCountry, validateDocument } from './documentCountries';
import { getHostBoardSlug } from '../jobBoard/hostSlug';

const OTHER = '__other__';

/**
 * "Completa tus datos" (PLAN_MASIVO_PORTAL): nacionalidad y documento con la
 * misma regla del bot. Organización en Chile (o sin vacante): chileno → RUT;
 * extranjero → "¿Tienes RUT chileno?" → RUT, o documento de su país /
 * pasaporte. Organización fuera de Chile → documento de su país (o
 * pasaporte), sin hablar de RUT.
 *
 * mode 'register': correo verificado sin cuenta → la cuenta se crea aquí con
 *   el permiso de registro (si la persona se va antes, no queda nada).
 * mode 'complete': cuenta antigua sin documento (ya tiene sesión).
 * Si el documento ya es de otra cuenta: se ofrece enviar el código a ese correo.
 */
const PortalIdentityStep = ({
  position, mode, email, registrationToken, sessionToken,
  onDone, onUseOtherAccount, onRestart,
}) => {
  const { t } = useTranslation();
  const ti = (k, o) => t(`portal.identityStep.${k}`, o);
  const isRegister = mode === 'register';

  const [nationalityChoice, setNationalityChoice] = useState('');
  const [nationalityOther, setNationalityOther] = useState('');
  const [nationalityError, setNationalityError] = useState('');
  const [hasRut, setHasRut] = useState('');
  const [docType, setDocType] = useState('national');
  const [dni, setDni] = useState('');
  const [dniError, setDniError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Documento de otra cuenta: { maskedEmail, request }
  const [inUse, setInUse] = useState(null);
  const [sendingOther, setSendingOther] = useState(false);
  // Permiso de registro vencido o usado
  const [expired, setExpired] = useState(false);

  // País de operación de la organización de la vacante (sin vacante o sin
  // país = Chile, como el bot)
  const orgCountry = String(position?.organizationCountry || 'CL').toUpperCase();
  const nationalityOption = NATIONALITY_OPTIONS.find((n) => n.label === nationalityChoice) || null;
  const nationalityCode = nationalityChoice === OTHER ? 'XX' : (nationalityOption?.code || null);
  const nationalityText = nationalityChoice === OTHER ? nationalityOther.trim() : (nationalityOption?.label || '');
  const asksHasRut = Boolean(nationalityCode) && orgCountry === 'CL' && nationalityCode !== 'CL';
  const usesRut = nationalityCode === 'CL' || (asksHasRut && hasRut === 'yes');
  // Pasaporte: alternativa para el extranjero (en el país de la organización) sin RUT
  const canUsePassport = Boolean(nationalityCode) && !usesRut && nationalityCode !== 'CL' && nationalityCode !== orgCountry;
  const isPassport = canUsePassport && docType === 'passport';
  const docCountry = usesRut ? 'CL' : nationalityCode;
  const nationalDocLabel = getCountry(docCountry)?.docLabel || t('portal.documentLabel');
  const docLabel = isPassport ? t('portal.access.passport') : nationalDocLabel;
  // El campo del documento aparece cuando ya se sabe cuál pedir
  const showDocument = Boolean(nationalityCode) && (!asksHasRut || hasRut !== '');

  const resetDocument = () => {
    setDni('');
    setDniError('');
    setError(null);
    setInUse(null);
  };

  const handleNationality = (value) => {
    setNationalityChoice(value);
    setNationalityError('');
    setHasRut('');
    setDocType('national');
    resetDocument();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDniError('');
    setNationalityError('');
    setError(null);
    setInUse(null);

    if (!nationalityChoice) {
      setNationalityError(t('portal.access.nationalityRequired'));
      return;
    }
    if (nationalityChoice === OTHER && !nationalityText) {
      setNationalityError(t('portal.access.nationalityOtherRequired'));
      return;
    }
    if (asksHasRut && !hasRut) {
      setError(t('portal.access.hasRutRequired'));
      return;
    }
    if (!dni.trim()) {
      setDniError(t('portal.documentRequired'));
      return;
    }
    // Pasaporte: formato libre (como "Otro"); el resto, el formato de su país
    const { valid, normalized } = validateDocument(dni, isPassport ? 'XX' : docCountry);
    if (!valid) {
      setDniError(t('portal.documentInvalid', { doc: docLabel }));
      return;
    }
    const request = {
      dni: normalized,
      country: docCountry,
      nationality: nationalityText,
      ...(isPassport ? { documentType: 'passport' } : {}),
    };

    setLoading(true);
    try {
      if (isRegister) {
        const slug = getHostBoardSlug();
        const { data } = await api.post(`/public/candidates/email/register`, {
          registrationToken, ...request, ...(slug ? { slug } : {}),
        });
        if (data.response?.status_response === 'OK') {
          onDone(data.response.sessionToken, data.response.candidateId);
          return;
        }
      } else {
        const { data } = await api.post(`/public/candidates/me/identity`, request, {
          headers: { 'x-candidate-token': sessionToken },
        });
        if (data.response?.status_response === 'OK') {
          onDone(data.response.data || null);
          return;
        }
      }
      setError(ti('saveError'));
    } catch (err) {
      console.error(err);
      const res = err.response?.data?.response;
      if (res?.errorType === 'DOCUMENT_IN_USE') {
        setInUse({ maskedEmail: res.maskedEmail || null, request });
      } else if (res?.errorType === 'REGISTRATION_EXPIRED') {
        setExpired(true);
      } else if (res?.errorType === 'ALREADY_HAS_DOCUMENT') {
        onDone(null);
      } else if (res?.errorType === 'EMAIL_IN_USE') {
        setError(t('portal.emailInUse'));
      } else if (err.response?.status === 429) {
        setError(t('portal.tooManyRequests'));
      } else {
        setError(ti('saveError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // El documento es de otra cuenta: código al correo de esa cuenta (acceso
  // por documento de siempre) y se entra a esa cuenta
  const handleUseOtherAccount = async () => {
    if (!inUse) return;
    setSendingOther(true);
    setError(null);
    try {
      const { data } = await api.post(`/public/candidates/request-otp`, inUse.request);
      if (data.response?.status_response === 'OK' && data.response.exists) {
        onUseOtherAccount({
          ...inUse.request,
          mode: 'document',
          email: null,
          maskedEmail: data.response.maskedEmail || inUse.maskedEmail,
          registeredVia: 'account',
        });
      } else {
        setError(t('portal.otpSendError'));
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.status === 429 ? t('portal.tooManyRequests') : t('portal.otpSendError'));
    } finally {
      setSendingOther(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', px: 2, pt: { xs: '6vh', md: '10vh' } }}>
      <Box sx={{ width: '100%', maxWidth: 480 }}>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/amandalogoh.png"
            alt="Amanda HR"
            sx={{ height: 40, mb: 3 }}
          />
          <Typography variant="h5" fontWeight={700}>
            {position ? position.name : t('portal.title')}
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            {ti('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isRegister ? ti('subtitleNew', { email }) : ti('subtitleComplete')}
          </Typography>

          {expired ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>{ti('expired')}</Alert>
              <Button fullWidth variant="contained" onClick={onRestart} sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5 }}>
                {ti('restart')}
              </Button>
            </>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              )}

              {inUse && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {ti('documentInUse', { email: inUse.maskedEmail || '' })}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleUseOtherAccount}
                    disabled={sendingOther}
                    startIcon={sendingOther ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    {ti('useThatAccount')}
                  </Button>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    {t('portal.noAccessToEmail')}
                  </Typography>
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  select
                  label={t('portal.access.nationality')}
                  value={nationalityChoice}
                  onChange={(e) => handleNationality(e.target.value)}
                  fullWidth
                  required
                  error={!!nationalityError && nationalityChoice !== OTHER}
                  helperText={nationalityChoice !== OTHER ? nationalityError : ''}
                  sx={{ mb: 2 }}
                >
                  {NATIONALITY_OPTIONS.map((n) => (
                    <MenuItem key={n.code} value={n.label}>{n.label}</MenuItem>
                  ))}
                  <MenuItem value={OTHER}>{t('portal.access.otherNationality')}</MenuItem>
                </TextField>

                {nationalityChoice === OTHER && (
                  <TextField
                    label={t('portal.access.nationalityOther')}
                    value={nationalityOther}
                    onChange={(e) => {
                      setNationalityOther(e.target.value);
                      setNationalityError('');
                    }}
                    fullWidth
                    required
                    error={!!nationalityError}
                    helperText={nationalityError}
                    inputProps={{ maxLength: 60 }}
                    sx={{ mb: 2 }}
                  />
                )}

                {asksHasRut && (
                  <FormControl sx={{ mb: 2, display: 'block' }}>
                    <FormLabel sx={{ fontSize: '0.875rem' }}>{t('portal.access.hasRut')}</FormLabel>
                    <RadioGroup
                      row
                      value={hasRut}
                      onChange={(e) => {
                        setHasRut(e.target.value);
                        setDocType('national');
                        resetDocument();
                      }}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio />} label={t('no')} />
                    </RadioGroup>
                  </FormControl>
                )}

                {showDocument && canUsePassport && (
                  <FormControl sx={{ mb: 2, display: 'block' }}>
                    <FormLabel sx={{ fontSize: '0.875rem' }}>{t('portal.access.documentType')}</FormLabel>
                    <RadioGroup
                      row
                      value={docType}
                      onChange={(e) => {
                        setDocType(e.target.value);
                        resetDocument();
                      }}
                    >
                      <FormControlLabel value="national" control={<Radio />} label={t('portal.access.nationalDoc', { doc: nationalDocLabel })} />
                      <FormControlLabel value="passport" control={<Radio />} label={t('portal.access.passport')} />
                    </RadioGroup>
                  </FormControl>
                )}

                {showDocument && (
                  <TextField
                    label={docLabel}
                    value={dni}
                    onChange={(e) => {
                      setDni(e.target.value);
                      setDniError('');
                      setInUse(null);
                    }}
                    fullWidth
                    required
                    error={!!dniError}
                    helperText={dniError || (usesRut ? t('portal.rutHint') : '')}
                    InputProps={{
                      startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
                    }}
                    sx={{ mb: 3 }}
                  />
                )}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5 }}
                >
                  {loading ? ti('saving') : isRegister ? ti('create') : ti('save')}
                </Button>
              </Box>

              {isRegister && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Link component="button" variant="body2" onClick={onRestart} sx={{ color: 'text.secondary' }}>
                    {t('portal.back')}
                  </Link>
                </Box>
              )}
            </>
          )}
        </Paper>

      </Box>
    </Box>
  );
};

export default PortalIdentityStep;
